import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

export const VisitFormDialog = ({ isOpen, onClose, onSubmit, sites, studies, visit = null, title, searchSites, searchStudies, getStudyAutoPopulateData, getSiteAutoPopulateData }) => {
  const [formData, setFormData] = useState({
    pi_name: visit?.pi_name || '',
    site_id: visit?.site_id || '',
    study_id: visit?.study_id || '',
    visit_type: visit?.visit_type || 'screening',
    patient_name: visit?.patient_name || '',
    contact: visit?.contact || '',
    date: visit?.date ? new Date(visit.date).toISOString().slice(0, 16) : '',
    status: visit?.status || 'ongoing',
    comment: visit?.comment || ''
  });

  const [availableSites, setAvailableSites] = useState(sites);
  const [availableStudies, setAvailableStudies] = useState(studies);
  const [searchTerms, setSearchTerms] = useState({ sites: '', studies: '' });

  const handleStudyChange = async (studyId) => {
    handleChange('study_id', studyId);
    try {
      const autoData = await getStudyAutoPopulateData(studyId);
      if (autoData) {
        if (autoData.related_sites && autoData.related_sites.length > 0) {
          setAvailableSites(autoData.related_sites);
          if (autoData.related_sites.length === 1) {
            const selectedSite = autoData.related_sites[0];
            handleChange('site_id', selectedSite.id);
            let piName = "";
            const siteName = selectedSite.name;
            if (siteName.includes("Dr.") || siteName.includes("Dr ")) {
              const parts = siteName.replace("Dr.", "Dr").split("Dr");
              if (parts.length > 1) piName = `Dr${parts[parts.length - 1].trim()}`;
            }
            if (piName) handleChange('pi_name', piName);
          }
        }
        if (autoData.related_pis && autoData.related_pis.length > 0) {
          handleChange('pi_name', autoData.related_pis[0]);
        }
        toast.success(`Auto-populated data for ${autoData.study_name}`);
      }
    } catch (error) {
      
      setAvailableSites(sites);
    }
  };

  const handleSiteChange = async (siteId) => {
    handleChange('site_id', siteId);
    const selectedSite = sites.find(site => site.id === siteId);
    if (selectedSite) {
      let piName = "";
      const siteName = selectedSite.name;
      if (siteName.includes("Dr.") || siteName.includes("Dr ")) {
        const parts = siteName.replace("Dr.", "Dr").split("Dr");
        if (parts.length > 1) piName = `Dr${parts[parts.length - 1].trim()}`;
      }
      if (piName) handleChange('pi_name', piName);
    }
    try {
      const autoData = await getSiteAutoPopulateData(siteId);
      if (autoData) {
        if (autoData.related_studies && autoData.related_studies.length > 0) {
          setAvailableStudies(autoData.related_studies);
          if (autoData.related_studies.length === 1) {
            handleChange('study_id', autoData.related_studies[0].id);
          }
        }
        toast.success(`Auto-populated data for ${autoData.site_name}`);
      }
    } catch (error) {
      
      setAvailableStudies(studies);
    }
  };

  const handleSiteSearch = async (searchTerm) => {
    setSearchTerms(prev => ({ ...prev, sites: searchTerm }));
    try {
      const searchResults = await searchSites(searchTerm);
      setAvailableSites(searchResults);
    } catch {
      toast.error("Failed to search sites");
    }
  };

  const handleStudySearch = async (searchTerm) => {
    setSearchTerms(prev => ({ ...prev, studies: searchTerm }));
    try {
      const searchResults = await searchStudies(searchTerm);
      setAvailableStudies(searchResults);
    } catch {
      toast.error("Failed to search studies");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setAvailableSites(sites);
      setAvailableStudies(studies);
      setSearchTerms({ sites: '', studies: '' });
    }
  }, [isOpen, sites, studies]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData, date: new Date(formData.date).toISOString() };
    onSubmit(submitData);
    setFormData({
      pi_name: '', site_id: '', study_id: '', visit_type: 'screening',
      patient_name: '', contact: '', date: '', status: 'ongoing', comment: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {visit ? 'Update visit information' : 'Create a new visit record'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pi_name">PI Name</Label>
              <Input id="pi_name" value={formData.pi_name} onChange={(e) => handleChange('pi_name', e.target.value)} placeholder="Enter PI name" required />
            </div>
            <div>
              <Label htmlFor="patient_name">Patient Name</Label>
              <Input id="patient_name" value={formData.patient_name} onChange={(e) => handleChange('patient_name', e.target.value)} placeholder="Enter patient name" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="site">Site (with smart search)</Label>
              <div className="space-y-2">
                <Input placeholder="Search sites..." value={searchTerms.sites} onChange={(e) => handleSiteSearch(e.target.value)} className="text-sm" />
                <Select value={formData.site_id} onValueChange={handleSiteChange}>
                  <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>
                    {availableSites.map(site => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name} {site.coordinator && `(${site.coordinator})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableSites.length !== sites.length && (
                  <p className="text-xs text-blue-600">Showing {availableSites.length} filtered results</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="study">Study (with smart search)</Label>
              <div className="space-y-2">
                <Input placeholder="Search studies..." value={searchTerms.studies} onChange={(e) => handleStudySearch(e.target.value)} className="text-sm" />
                <Select value={formData.study_id} onValueChange={handleStudyChange}>
                  <SelectTrigger><SelectValue placeholder="Select study" /></SelectTrigger>
                  <SelectContent>
                    {availableStudies.map(study => (
                      <SelectItem key={study.id} value={study.id}>
                        {study.name} {study.protocol_number && `(${study.protocol_number})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableStudies.length !== studies.length && (
                  <p className="text-xs text-blue-600">Showing {availableStudies.length} filtered results</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="visit_type">Visit Type</Label>
              <Select value={formData.visit_type} onValueChange={(value) => handleChange('visit_type', value)}>
                <SelectTrigger><SelectValue placeholder="Select visit type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="selection_visit">Selection Visit</SelectItem>
                  <SelectItem value="siv">SIV</SelectItem>
                  <SelectItem value="screening">Screening</SelectItem>
                  <SelectItem value="baseline">Baseline</SelectItem>
                  <SelectItem value="visit_number">Visit Number</SelectItem>
                  <SelectItem value="imv">IMV</SelectItem>
                  <SelectItem value="cov">COV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Visit Date</Label>
              <Input id="date" type="datetime-local" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="contact">Contact</Label>
              <Input id="contact" value={formData.contact} onChange={(e) => handleChange('contact', e.target.value)} placeholder="Contact information" />
            </div>
          </div>

          <div>
            <Label htmlFor="comment">Comment</Label>
            <Textarea id="comment" value={formData.comment} onChange={(e) => handleChange('comment', e.target.value)} placeholder="Additional comments (optional)" rows={3} />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">{visit ? 'Update Visit' : 'Create Visit'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
